'use client';

import { shouldApplyCoatingSelection } from '@/app/_lens-components/utils';
import {
  COATING_COMPONENT,
  EDIT_PRESCRIPTION_COMPONENT,
  hasReachedLensProcessStep,
  LENS_INDEX_COMPONENT,
  LENS_TYPE_COMPONENT,
} from '@/app/_lens-components/constants';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { EPrescriptionType } from '@/utils/enums/goods';
import { useMemo } from 'react';

export interface UseShowLensInfoResult {
  showPrescription: boolean;
  showLens: boolean;
  showLensIndex: boolean;
  showCoating: boolean;
}

/** 按当前配镜步骤（nowComponent）控制 LensInfo 各区块是否展示 */
export function useShowLensInfo(isFrameProcess: boolean): UseShowLensInfoResult {
  const {
    prescriptionType,
    lensIndexPriceInfo,
    selectedLensIndexItem,
    selectedCoating,
    selectedColorItem,
    selectedAdvancedLensIndexItem,
    selectedLensType,
    selectedBrand,
    nowComponent,
  } = useLensProcessStore();

  return useMemo(() => {
    const showPrescription =
      !isFrameProcess &&
      !!prescriptionType &&
      prescriptionType !== EPrescriptionType.READING_GLASSES &&
      hasReachedLensProcessStep(nowComponent, EDIT_PRESCRIPTION_COMPONENT);

    const hasLensSelectionToShow = !!(
      selectedAdvancedLensIndexItem ||
      selectedLensType?.merge_brand === 2 ||
      (selectedLensType?.merge_brand === 1 && selectedBrand?.brand_name) ||
      (isFrameProcess && selectedColorItem)
    );
    // frame-process（仅镜框）处方类型恒为 FRAME_ONLY，仍需在左侧展示已选镜片类型
    const showLens =
      hasReachedLensProcessStep(nowComponent, LENS_TYPE_COMPONENT) &&
      hasLensSelectionToShow &&
      (isFrameProcess || prescriptionType !== EPrescriptionType.FRAME_ONLY);

    // 仅镜框：allComponent 仅在 lens_list.length > 1 时含折射率步；无该步时镀膜 rank 仍会 >= LENS_INDEX，需额外约束
    const frameProcessHasLensIndexStep = (selectedColorItem?.lens_list?.length ?? 0) > 1;
    const showLensIndex =
      hasReachedLensProcessStep(nowComponent, LENS_INDEX_COMPONENT) &&
      !!selectedLensIndexItem &&
      !!lensIndexPriceInfo &&
      (!isFrameProcess || frameProcessHasLensIndexStep);

    const showCoating =
      hasReachedLensProcessStep(nowComponent, COATING_COMPONENT) &&
      shouldApplyCoatingSelection(prescriptionType, selectedColorItem, { isFrameProcess }) &&
      !!selectedCoating;

    return { showPrescription, showLens, showLensIndex, showCoating };
  }, [
    isFrameProcess,
    prescriptionType,
    nowComponent,
    selectedAdvancedLensIndexItem,
    selectedLensType,
    selectedBrand,
    selectedLensIndexItem,
    lensIndexPriceInfo,
    selectedCoating,
    selectedColorItem,
  ]);
}
